/**
 * Guest Lost & Found Claim Public Landing Page (/claim/<token>).
 * R39 K7.b (SO-P115, SO-P116; SO-D207, SO-D294, SO-D296, SO-D297).
 */

import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useNls } from '../../nls/nlsProvider'
import type { NlsKey } from '../../nls/strings_nls'
import {
  getLostClaimLinkInfo,
  prepareLostClaimOtp,
  openLostClaim,
  describeLostClaim,
} from '../../services/opsLostFoundService'
import { sendOutsidePhoneOtp } from '../../services/outsidePhoneOtp'
import { getApprovalsAuth, signOutApprovals } from '../../services/approvalsApp'
import type { ConfirmationResult } from 'firebase/auth'
import type { LostFoundCategory, LostFoundReturnMode } from '../../types/ops'
import { Button } from '../Common/Button'
import './claimLanding.css'

export interface ClaimLandingProps {
  token: string
}

type Step = 'LOADING' | 'INVALID' | 'PREVIEW' | 'OTP_ENTRY' | 'FORM' | 'THANK_YOU'

function createOpId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

function translateCategory(category: LostFoundCategory, t: (key: NlsKey) => string): string {
  switch (category) {
    case 'CLOTHING':
      return t('NLSOpsStaysLostCatClothing')
    case 'ELECTRONICS':
      return t('NLSOpsStaysLostCatElectronics')
    case 'DOCUMENTS':
      return t('NLSOpsStaysLostCatDocuments')
    case 'JEWELLERY':
      return t('NLSOpsStaysLostCatJewellery')
    case 'MONEY':
      return t('NLSOpsStaysLostCatMoney')
    case 'KEYS':
      return t('NLSOpsStaysLostCatKeys')
    case 'BAGS':
      return t('NLSOpsStaysLostCatBags')
    case 'OTHER':
    default:
      return t('NLSOpsStaysLostCatOther')
  }
}

export function ClaimLanding({ token }: ClaimLandingProps): React.ReactElement {
  const { t, locale } = useNls()

  const [step, setStep] = useState<Step>('LOADING')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<boolean>(false)

  // Step 1 preview data
  const [linkInfo, setLinkInfo] = useState<{
    propertyName: string
    category: LostFoundCategory
    phoneMasked: string
    linkExpiresAt: number
  } | null>(null)

  // Step 2 OTP state
  const [phone, setPhone] = useState<string>('')
  const [otpCode, setOtpCode] = useState<string>('')
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null)

  // Step 3 Claim form state
  const [guestDescription, setGuestDescription] = useState<string>('')
  const [returnMode, setReturnMode] = useState<LostFoundReturnMode>('PICKUP')
  const [addressLine1, setAddressLine1] = useState<string>('')
  const [addressLine2, setAddressLine2] = useState<string>('')
  const [addressLine3, setAddressLine3] = useState<string>('')
  const [city, setCity] = useState<string>('')
  const [pin, setPin] = useState<string>('')
  const [state, setState] = useState<string>('')

  // opId kept across retries
  const opIdRef = useRef<string>(createOpId())

  const formatExpiry = useCallback(
    (timestamp: number) => {
      try {
        return new Intl.DateTimeFormat(locale, {
          dateStyle: 'medium',
          timeStyle: 'short',
        }).format(new Date(timestamp))
      } catch {
        return new Date(timestamp).toLocaleString()
      }
    },
    [locale],
  )

  // Initial link info fetch
  useEffect(() => {
    let cancelled = false

    async function loadLink() {
      if (!token) {
        setStep('INVALID')
        return
      }

      try {
        const info = await getLostClaimLinkInfo({ token })
        if (cancelled) return

        if (info.linkExpiresAt <= Date.now()) {
          setStep('INVALID')
          return
        }

        setLinkInfo(info)
        setStep('PREVIEW')
      } catch {
        if (cancelled) return
        setStep('INVALID')
      }
    }

    void loadLink()

    return () => {
      cancelled = true
    }
  }, [token])

  // Handle Send OTP
  const handleSendOtp = async () => {
    if (!linkInfo) return
    setError(null)

    const expectedLast4 = linkInfo.phoneMasked.replace(/\D/g, '').slice(-4)
    const enteredDigits = phone.replace(/\D/g, '')
    const enteredLast4 = enteredDigits.slice(-4)

    if (enteredLast4 !== expectedLast4 || enteredDigits.length < 7) {
      setError(t('NLSOpsApprovalPhoneMismatch'))
      return
    }

    setBusy(true)
    try {
      const prep = await prepareLostClaimOtp({ token })
      if (!prep.success || prep.attemptsRemaining <= 0) {
        setError(t('NLSOpsStaysClaimCapExhausted'))
        setBusy(false)
        return
      }

      const formattedPhone = phone.trim().startsWith('+') ? phone.trim() : `+${phone.trim()}`
      const confirmation = await sendOutsidePhoneOtp(getApprovalsAuth(), formattedPhone)
      setConfirmationResult(confirmation)
      setStep('OTP_ENTRY')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      if (
        msg.includes('resource-exhausted') ||
        msg.includes('too-many-requests') ||
        msg.includes('attempts')
      ) {
        setError(t('NLSOpsStaysClaimCapExhausted'))
      } else {
        setError(t('NLSOpsApprovalGenericError'))
      }
    } finally {
      setBusy(false)
    }
  }

  // Handle Verify OTP
  const handleVerifyOtp = async () => {
    if (!confirmationResult) return
    setError(null)

    if (!otpCode || otpCode.trim().length !== 6) {
      setError(t('NLSOpsApprovalInvalidCode'))
      return
    }

    setBusy(true)
    try {
      await confirmationResult.confirm(otpCode.trim())
      await openLostClaim({ token })
      setStep('FORM')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      if (msg.includes('invalid-verification-code') || msg.includes('code')) {
        setError(t('NLSOpsApprovalInvalidCode'))
      } else {
        setError(t('NLSOpsApprovalGenericError'))
      }
    } finally {
      setBusy(false)
    }
  }

  // Handle Submit Claim
  const handleSubmitClaim = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const desc = guestDescription.trim()
    if (desc.length < 3 || desc.length > 1000) {
      setError(t('NLSOpsStaysLostErrInvalidArg'))
      return
    }

    if (returnMode === 'COURIER') {
      const l1 = addressLine1.trim()
      const l2 = addressLine2.trim()
      const c = city.trim()
      const p = pin.trim()
      const s = state.trim()

      if (!l1 || !l2 || !c || !s) {
        setError(t('NLSOpsStaysLostErrInvalidArg'))
        return
      }
      if (!/^\d{6}$/.test(p)) {
        setError(t('NLSOpsStaysLostErrInvalidArg'))
        return
      }
    }

    setBusy(true)
    try {
      const courierAddress =
        returnMode === 'COURIER'
          ? {
              lines: [
                addressLine1.trim(),
                addressLine2.trim(),
                ...(addressLine3.trim() ? [addressLine3.trim()] : []),
              ],
              city: city.trim(),
              pin: pin.trim(),
              state: state.trim(),
            }
          : undefined

      await describeLostClaim({
        token,
        guestDescription: desc,
        returnMode,
        courierAddress,
        opId: opIdRef.current,
      })

      // Sign out from approvals auth after completion
      try {
        await signOutApprovals()
      } catch {
        // ignore sign-out cleanup errors
      }

      setStep('THANK_YOU')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      if (msg.includes('already been described')) {
        setStep('THANK_YOU')
      } else {
        setError(t('NLSOpsApprovalGenericError'))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="claimLanding__container" aria-label={t('NLSOpsStaysClaimLandingTitle')}>
      <div className="claimLanding__card">
        {step === 'LOADING' && (
          <div className="claimLanding__header">
            <h1 className="claimLanding__title">{t('NLSOpsPlaceScanLoading')}</h1>
          </div>
        )}

        {step === 'INVALID' && (
          <div className="claimLanding__invalid">
            <h1 className="claimLanding__title">{t('NLSOpsStaysClaimInvalidTitle')}</h1>
            <p className="claimLanding__messageBody">{t('NLSOpsStaysClaimInvalidBody')}</p>
          </div>
        )}

        {step === 'PREVIEW' && linkInfo && (
          <>
            <div className="claimLanding__header">
              <h1 className="claimLanding__title">{t('NLSOpsStaysClaimLandingTitle')}</h1>
              <p className="claimLanding__subtitle">{linkInfo.propertyName}</p>
            </div>

            {error && (
              <div className="claimLanding__errorBanner" role="alert">
                {error}
              </div>
            )}

            <div className="claimLanding__infoGrid">
              <div className="claimLanding__infoRow">
                <span className="claimLanding__infoLabel">{t('NLSOpsStaysClaimPropertyLabel')}</span>
                <span className="claimLanding__infoValue">{linkInfo.propertyName}</span>
              </div>
              <div className="claimLanding__infoRow">
                <span className="claimLanding__infoLabel">{t('NLSOpsStaysClaimCategoryLabel')}</span>
                <span className="claimLanding__infoValue">{translateCategory(linkInfo.category, t)}</span>
              </div>
              <div className="claimLanding__infoRow">
                <span className="claimLanding__infoLabel">{t('NLSOpsStaysClaimPhoneMaskedLabel')}</span>
                <span className="claimLanding__infoValue">{linkInfo.phoneMasked}</span>
              </div>
              <div className="claimLanding__infoRow">
                <span className="claimLanding__infoLabel">{t('NLSOpsStaysClaimExpiryLabel')}</span>
                <span className="claimLanding__infoValue">{formatExpiry(linkInfo.linkExpiresAt)}</span>
              </div>
            </div>

            <div className="claimLanding__form">
              <div className="claimLanding__field">
                <label htmlFor="claim-phone-input" className="claimLanding__label">
                  {t('NLSOpsStaysLostClaimantPhoneLabel')}
                </label>
                <input
                  id="claim-phone-input"
                  type="tel"
                  className="claimLanding__input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+91 98765 43210"
                  disabled={busy}
                />
              </div>

              <div id="outside-recaptcha-container" />

              <div className="claimLanding__actions">
                <Button
                  variant="primary"
                  size="lg"
                  loading={busy}
                  disabled={busy || phone.replace(/\D/g, '').length < 7}
                  onClick={handleSendOtp}
                >
                  {t('NLSOpsStaysClaimSendOtpBtn')}
                </Button>
              </div>
            </div>
          </>
        )}

        {step === 'OTP_ENTRY' && (
          <>
            <div className="claimLanding__header">
              <h1 className="claimLanding__title">{t('NLSOpsStaysClaimLandingTitle')}</h1>
              <p className="claimLanding__subtitle">{t('NLSOpsStaysClaimOtpCodeLabel')}</p>
            </div>

            {error && (
              <div className="claimLanding__errorBanner" role="alert">
                {error}
              </div>
            )}

            <div className="claimLanding__form">
              <div className="claimLanding__field">
                <label htmlFor="claim-otp-input" className="claimLanding__label">
                  {t('NLSOpsStaysClaimOtpCodeLabel')}
                </label>
                <input
                  id="claim-otp-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  className="claimLanding__input"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  disabled={busy}
                />
              </div>

              <div className="claimLanding__actions">
                <Button
                  variant="primary"
                  size="lg"
                  loading={busy}
                  disabled={busy || otpCode.trim().length !== 6}
                  onClick={handleVerifyOtp}
                >
                  {t('NLSOpsStaysClaimVerifyOtpBtn')}
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  disabled={busy}
                  onClick={handleSendOtp}
                >
                  {t('NLSOpsApprovalResendCode')}
                </Button>
              </div>
            </div>
          </>
        )}

        {step === 'FORM' && (
          <form className="claimLanding__form" onSubmit={handleSubmitClaim}>
            <div className="claimLanding__header">
              <h1 className="claimLanding__title">{t('NLSOpsStaysClaimFormTitle')}</h1>
              <p className="claimLanding__help">{t('NLSOpsStaysClaimDescHelp')}</p>
            </div>

            {error && (
              <div className="claimLanding__errorBanner" role="alert">
                {error}
              </div>
            )}

            <div className="claimLanding__field">
              <label htmlFor="claim-desc-textarea" className="claimLanding__label">
                {t('NLSOpsStaysClaimFormTitle')}
              </label>
              <textarea
                id="claim-desc-textarea"
                className="claimLanding__textarea"
                value={guestDescription}
                onChange={(e) => setGuestDescription(e.target.value)}
                placeholder={t('NLSOpsStaysClaimDescPlaceholder')}
                maxLength={1000}
                disabled={busy}
                required
              />
            </div>

            <div className="claimLanding__field">
              <label className="claimLanding__label">{t('NLSOpsStaysClaimReturnModeLabel')}</label>
              <div className="claimLanding__radioGroup">
                <label className="claimLanding__radioOption">
                  <input
                    type="radio"
                    name="returnMode"
                    value="PICKUP"
                    checked={returnMode === 'PICKUP'}
                    onChange={() => setReturnMode('PICKUP')}
                    disabled={busy}
                  />
                  <span>{t('NLSOpsStaysClaimReturnPickup')}</span>
                </label>
                <label className="claimLanding__radioOption">
                  <input
                    type="radio"
                    name="returnMode"
                    value="COURIER"
                    checked={returnMode === 'COURIER'}
                    onChange={() => setReturnMode('COURIER')}
                    disabled={busy}
                  />
                  <span>{t('NLSOpsStaysClaimReturnCourier')}</span>
                </label>
              </div>
            </div>

            {returnMode === 'COURIER' && (
              <>
                <div className="claimLanding__field">
                  <label htmlFor="claim-addr-1" className="claimLanding__label">
                    {t('NLSOpsStaysClaimAddressLine1')}
                  </label>
                  <input
                    id="claim-addr-1"
                    type="text"
                    className="claimLanding__input"
                    value={addressLine1}
                    onChange={(e) => setAddressLine1(e.target.value)}
                    maxLength={200}
                    disabled={busy}
                    required
                  />
                </div>

                <div className="claimLanding__field">
                  <label htmlFor="claim-addr-2" className="claimLanding__label">
                    {t('NLSOpsStaysClaimAddressLine2')}
                  </label>
                  <input
                    id="claim-addr-2"
                    type="text"
                    className="claimLanding__input"
                    value={addressLine2}
                    onChange={(e) => setAddressLine2(e.target.value)}
                    maxLength={200}
                    disabled={busy}
                    required
                  />
                </div>

                <div className="claimLanding__field">
                  <label htmlFor="claim-addr-3" className="claimLanding__label">
                    {t('NLSOpsStaysClaimAddressLine3')}
                  </label>
                  <input
                    id="claim-addr-3"
                    type="text"
                    className="claimLanding__input"
                    value={addressLine3}
                    onChange={(e) => setAddressLine3(e.target.value)}
                    maxLength={200}
                    disabled={busy}
                  />
                </div>

                <div className="claimLanding__field">
                  <label htmlFor="claim-city" className="claimLanding__label">
                    {t('NLSOpsStaysClaimCity')}
                  </label>
                  <input
                    id="claim-city"
                    type="text"
                    className="claimLanding__input"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    maxLength={100}
                    disabled={busy}
                    required
                  />
                </div>

                <div className="claimLanding__field">
                  <label htmlFor="claim-pin" className="claimLanding__label">
                    {t('NLSOpsStaysClaimPin')}
                  </label>
                  <input
                    id="claim-pin"
                    type="text"
                    inputMode="numeric"
                    className="claimLanding__input"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="110001"
                    maxLength={6}
                    disabled={busy}
                    required
                  />
                </div>

                <div className="claimLanding__field">
                  <label htmlFor="claim-state" className="claimLanding__label">
                    {t('NLSOpsStaysClaimState')}
                  </label>
                  <input
                    id="claim-state"
                    type="text"
                    className="claimLanding__input"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    maxLength={100}
                    disabled={busy}
                    required
                  />
                </div>
              </>
            )}

            <p className="claimLanding__privacyLine">{t('NLSOpsStaysClaimPrivacyLine')}</p>

            <div className="claimLanding__actions">
              <Button
                variant="primary"
                size="lg"
                type="submit"
                disabled={
                  busy ||
                  guestDescription.trim().length < 3 ||
                  (returnMode === 'COURIER' &&
                    (!addressLine1.trim() ||
                      !addressLine2.trim() ||
                      !city.trim() ||
                      !/^\d{6}$/.test(pin.trim()) ||
                      !state.trim()))
                }
              >
                {busy ? t('NLSOpsStaysRoomTypesSaving') : t('NLSOpsStaysClaimSubmitBtn')}
              </Button>
            </div>
          </form>
        )}

        {step === 'THANK_YOU' && (
          <div className="claimLanding__thankYou">
            <h1 className="claimLanding__title">{t('NLSOpsStaysClaimThankYouTitle')}</h1>
            <p className="claimLanding__messageBody">{t('NLSOpsStaysClaimThankYouBody')}</p>
          </div>
        )}
      </div>
    </main>
  )
}
export default ClaimLanding
